import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewEncapsulation,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import _isNull from 'lodash/isNull';
import _isEmpty from 'lodash/isEmpty';
import { FormioCustomComponent, DateHelperService } from 'dp-formio';
import { Utils } from 'formiojs';

@Component({
	selector: 'app-hijri-date',
	templateUrl: './hijri-date.component.html',
	encapsulation: ViewEncapsulation.None,
})
export class HijriDateComponent implements FormioCustomComponent<any>, OnInit, OnDestroy {
	hijriDate: any;
	dir: 'rtl' | 'ltr';
	@Input() required: boolean;
	@Input() value: any = {};
	@Output() valueChange = new EventEmitter<any>();
	@Input() disabled: boolean;
	@Input() viewOnly: boolean;
	@Input() data: any;
	@Input() customOptions: any;
	@Input() formioComponent: any;

	minHijri: any;
	maxHijri: any;
	minGreg: any;
	maxGreg: any;

	private subs: Subscription[] = [];
	set sub(s: Subscription) {
		this.subs.push(s);
	}

	constructor(
		private translateService: TranslateService,
		private dateHelperService: DateHelperService
	) {
		this.changeDirection(this.translateService.currentLang);
		this.sub = this.translateService.onLangChange.subscribe((data) => {
			this.changeDirection(data.lang);
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		// Min Hijri
		if (this.customOptions?.minHijri && this.customOptions?.minHijri == 'today') {
			this.minHijri = this.getTodayHijri();
		} else if (this.customOptions?.minHijri && 'toString' in this.customOptions?.minHijri) {
			this.minHijri = this.customOptions?.minHijri;
		}
		// Max Hijri
		if (this.customOptions?.maxHijri && this.customOptions?.maxHijri == 'today') {
			this.maxHijri = this.getTodayHijri();
		} else if (this.customOptions?.maxHijri && 'toString' in this.customOptions?.maxHijri) {
			this.maxHijri = this.customOptions?.maxHijri;
		}

		// Min Greg
		if (this.customOptions?.minGreg && this.customOptions?.minGreg == 'today') {
			this.minGreg = this.getTodayGreg();
		} else if (this.customOptions?.minGreg && 'toString' in this.customOptions?.minGreg) {
			this.minGreg = this.customOptions?.minGreg;
		}
		// Max Greg
		if (this.customOptions?.maxGreg && this.customOptions?.maxGreg == 'today') {
			this.maxGreg = this.getTodayGreg();
		} else if (this.customOptions?.maxGreg && 'toString' in this.customOptions?.maxGreg) {
			this.maxGreg = this.customOptions?.maxGreg;
		}

		if (this.value) this.hijriDate = this.value;
	}

	getTodayHijri(date?, lang?) {
		var date = date || new Date();
		lang = lang || 'en';
		var options = {
			year: 'numeric',
			month: 'numeric',
			day: 'numeric',
		};

		const todayHijri = date.toLocaleString(lang + '-u-ca-islamic', options);
		return {
			year: +todayHijri.split('/')[2].split(' ')[0],
			month: +todayHijri.split('/')[0],
			day: +todayHijri.split('/')[1] - 1,
		};
	}

	getTodayGreg() {
		var today = new Date();
		return {
			year: today.getFullYear(),
			month: today.getMonth() + 1,
			day: today.getDate(),
		};
	}

	ngOnInit(): void {}

	onDateChanged(selectedDate: any) {
		if (this.required && (_isNull(selectedDate) || _isEmpty(selectedDate))) {
			this.notifyValue(null);
			return;
		}
		this.notifyValue(selectedDate);
	}

	notifyValue(date: any) {
		if (date && this.customOptions.compineDate && !this.customOptions?.disableConverter) {
			this.value = this.convertDateToHijriAndGerg(date);
		} else {
			this.value = date;
		}
		this.valueChange.emit(this.value);
		this.afterChangeValue();
	}

	private afterChangeValue() {
		if (this.customOptions && this.customOptions.afterChange)
			this.customOptions.afterChange.forEach((item) => {
				if (this.formioComponent.root.wizard) {
					const pages = this.formioComponent.root.getFastPages({
						all: true,
						withoutFirstLoad: true,
					});
					const components = Utils.searchComponents(pages, { key: item.key });
					components.forEach((c) => {
						if (item.checkConditions) {
							c.checkConditions();
						} else if (item.calculateValue) {
							c.calculateValue(this.data);
						} else {
							const value = Utils.evaluate(item.value, {
								data: this.data,
								instance: this.formioComponent,
							});
							c.setValue(value);
						}
					});
				} else {
					const nestedForms = Utils.searchComponents(this.formioComponent.root.components, {
						type: 'form',
						'component.display': 'wizard',
					});
					nestedForms.forEach((formComponent) => {
						formComponent.subFormReady.then((subForm) => {
							const pages = subForm.getFastPages({ all: true, withoutFirstLoad: true });
							const components = Utils.searchComponents(pages, { key: item.key });
							components.forEach((c) => {
								if (item.checkConditions) {
									c.checkConditions();
								} else if (item.calculateValue) {
									c.calculateValue(this.data);
								} else {
									const value = Utils.evaluate(item.value, {
										data: this.data,
										instance: this.formioComponent,
									});
									c.setValue(value);
								}
							});
						});
					});

					const components = Utils.searchComponents(this.formioComponent.root.components, {
						key: item.key,
					});
					components.forEach((c) => {
						if (item.checkConditions) {
							c.checkConditions();
						}
						if (item.redraw) {
							if (c.component.type == 'datetime') c.buildBefore = false;
							c.redraw(true);
						} else if (item.calculateValue) {
							c.calculateValue(this.data);
						} else {
							const value = Utils.evaluate(item.value, {
								data: this.data,
								instance: this.formioComponent,
							});
							c.setValue(value);
						}
					});
				}
			});
	}

	convertDateToHijriAndGerg(selectedDate: any): {} {
		const hijriDate = this.dateHelperService.gregorianToHijri(selectedDate, 'iM/iD/iYYYY');

		return {
			gregorian: selectedDate,
			hijri: hijriDate,
		};
	}

	private changeDirection(lang) {
		this.dir = null;
		setTimeout(() => {
			this.dir = lang === 'ar' ? 'rtl' : 'ltr';
		});
	}

	ngOnDestroy() {
		this.subs.forEach((s) => s.unsubscribe());
		this.subs = [];
	}
}
